import { create, tsx } from '@dojo/framework/core/vdom';
import { Link } from '@dojo/framework/routing/Link';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { Summary } from '../../widgets/Summary';
import Image from '../../widgets/Image';

import * as css from './WebApplicationDevelopment.m.css';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import WhySitePen from '../../widgets/WhySitePen';

const engineeringImg = require('../../assets/images/illustration/engineering.svg');
const jsTechnologiesImg = require('../../assets/images/logo-illustration/js-technologies-color.svg');
const performanceImg = require('../../assets/images/illustration/high-performance.svg');
const softwareCycleImg = require('../../assets/images/illustration/software-cycle.svg');
const teamImg = require('../../assets/images/illustration/team-efficiency.svg');

const factory = create();

export const WebApplicationDevelopment = factory(function WebApplicationDevelopment() {
	return (
		<div>
			<Head
				title="Web Application Development Services | SitePen"
				description="SitePen is a web application development company building enterprise solutions to improve processes, streamline operations, and empower business success."
			/>

			<Hero background="level3Red">
				{{
					xlTitle: (
						<div>
							Web Application
							<br />
							Development
						</div>
					),
					blurb:
						'SitePen offers best in class expertise in web application development for the most discerning clients across the globe.'
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<Summary reverse paddingBottom="md">
						{{
							title: 'Develop a Competitive Edge',
							blurb: (
								<virtual>
									<p>
										Depending on the type of business you own, web application
										development can be one of the most powerful components of
										your software ecosystem. Web apps are used for a number of
										different businesses from construction lenders to social
										networking applications, and can help enhance business
										productivity, increase customer engagement, or allow
										businesses to deploy innovative ideas into entirely new
										markets.
									</p>
									<p>
										Web applications are run completely within a web browser
										like Chrome, Firefox and Safari with a backend service
										running on a web server. Modern advancements in browser
										technologies, along with consistent improvements in
										computing devices, internet bandwidth and security standards
										have allowed web apps to evolve to replace clunky and dated
										desktop applications.
									</p>
								</virtual>
							),
							image: <Image src={performanceImg} size="lg" />
						}}
					</Summary>
					<Summary smaller>
						{{
							blurb: (
								<virtual>
									<p>
										The advantage of web applications is that they are
										ubiquitous, accessible across many devices with the only
										requirement being a web browser. This allows companies to
										provide the maximum outreach and the least amount of
										friction for customers to interact with their brand, product
										and services. And with the advent and increasing adoption of
										Progressive Web Apps (PWAs), companies are now provided with
										even more capabilities to meet the needs of their users and
										business operations.
									</p>
									<p>
										A good web application development company will be able to
										advise and know how to best execute a web application so
										that it can run efficiently across multiple browser
										environments, both on and offline.
									</p>
								</virtual>
							),
							image: <Image src={teamImg} size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={softwareCycleImg} lazy />,
							title: 'How Are Web Applications Developed?',
							blurb: (
								<virtual>
									<p>
										Web applications are developed by companies that specialize
										in web app development services. Initially, a web app
										development company will consult with its client to
										understand the product requirements and develop a
										specifications document such as a product requirements
										document, scoping document or wireframe. Significantly
										important to this process is the careful consideration
										around the user interface, as all web apps require an
										intuitive interface layer to enable simplified interaction
										with the user.
									</p>
									<p>
										From there both the web developer and the client can
										mutually agree on the functionality of the product where it
										is then architected by a web app development team into its
										discrete components such as front end, APIs, and data model.
										Related to this process would be choosing an appropriate
										cloud services platform provider such as Amazon Web
										Services, and designing the infrastructure to host the
										application.
									</p>
									<p>
										The web app is then built by these teams under the direction
										of a project manager and a technical lead. The client will
										have the opportunity to provide feedback, input and
										direction throughout this entire process.
									</p>
									<p>
										Typically, a web application will be available for internal
										and client testing before being deployed to "production" or
										the live environment for public visibility. After this, the
										web app can continue to be enhanced, iterated upon, or
										otherwise maintained and supported as it sees use with a
										client's customer base.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper size="smaller">
					<Summary stack>
						{{
							image: <Image src={engineeringImg} lazy size="lg" />,
							title: 'How Long Does It Take to Develop A Web Application?',
							blurb: (
								<virtual>
									<p>
										Web application development can vary in timeframe from a few
										weeks to over a year, depending on the complexity of an
										application. Enterprise systems with complex functionality
										spread across multiple disciplines, departments and product
										features may evolve over the course of years.
									</p>
									<p>
										The reality is that most web apps are developed in a
										timeframe somewhere in between these two with most minimum
										viable web applications taking only a few months to build.
									</p>
									<p>
										A <Link to="contact">brief discussion with SitePen</Link>{' '}
										about your project can help us to determine a rough range of
										the time in which you should expect the entire design and
										development process to take. We have worked to build
										large-scale enterprise applications and web apps for
										startups alike and have a good idea of the time requirements
										for projects of all sizes. Our full-service team has the
										capability to take your idea from concept to reality in the
										shortest time possible.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<Summary reverse collapse="xLarge">
						{{
							title: 'Technologies We Use',
							blurb: (
								<virtual>
									<p>
										SitePen builds adaptable, performant and highly-scalable web
										applications using a suite of modern development frameworks.
										We have <Link to="expertise">core expertise</Link> in
										JavaScript and TypeScript and utilize these web frameworks
										and the extended JavaScript ecosystem to deliver software
										excellence to our clients. With SitePen as your partner, you
										can be confident that we will create a well-designed and a
										sustainable web application the right way on the first go.
									</p>
									<p>
										Javascript-based languages have the benefit of being able to
										be deployed from web applications to mobile applications and
										even desktop applications. This approach allows us to use a
										web-centric development language to build even the most
										complex and robust web applications, while being adaptable
										across multiple platforms, ensuring future proofing and the
										best possible uptake by your customer.
									</p>
									<p>
										Below are just a few examples of frameworks and languages we
										use:
									</p>
									<ul classes={[css.techList]}>
										<li>React</li>
										<li>Vue</li>
										<li>Node</li>
										<li>TypeScript</li>
										<li>Angular</li>
										<li>Dojo</li>
									</ul>
									<p>
										As an expert in web app development services, we believe we
										can use these languages to build just about every imaginable
										type of web app. Our collection of expertise allows us to
										leverage these frameworks to build both front-end solutions
										as well as the backend infrastructure that powers them. We
										are thought leaders in web app development, cross platform
										app development and cloud platform development services, and
										can provide our technical expertise to you at any point in
										the product and software development lifecycles.
									</p>
								</virtual>
							),
							image: <Image src={jsTechnologiesImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<WhySitePen>
						{{
							blurb: (
								<virtual>
									<p>
										SitePen is a{' '}
										<Link to="about">leading web app development</Link> company
										based in the USA. We have expertise in providing web app
										development services and have been helping our customers
										develop software over the past 20 years.
									</p>
									<p>
										Web app development is not just about writing software code,
										we also focus considerable effort in the user interface,
										meaning we utilize our team of designers to sit down with
										your company and walk through the best approach in turning
										your idea, or business objective into a meaningful design.
										Design is a key component of web application development.
									</p>
									<p>
										We can provide our development solutions to enterprise
										clients and small businesses, alike. We've executed high
										performing software solutions for clients like Fannie Mae,
										and Marriott International, and have developed award-winning
										products for countless small businesses and startups. As an
										experienced team we have a formal design, development and
										testing process and utilize best practices in project
										management and software development. We are experts in Agile
										Project Management, allowing us to work closely with our
										clients to ideate, iterate, and build in a seamless and
										collaborative process.
									</p>
									<p>
										We offer a quick turnaround on quotes, and provide some of
										the most competitive rates and prices in our market. While
										other companies utilize bulky, and dated software languages,
										SitePen operates entirely within a lean ecosystem of light
										client but highly robust frameworks.
									</p>
								</virtual>
							)
						}}
					</WhySitePen>
				</ContentWrapper>
			</Section>
			<Section type="trailing" shaded>
				<ConnectStrip trailing />
			</Section>
		</div>
	);
});

export default WebApplicationDevelopment;
